import React, { useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, type SubmitHandler } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  FulfillmentCreateRequest,
  fulfillmentCreateSchema,
  fulfillmentUpdateSchema,
  type Fulfillment,
  type FulfillmentCreateFormValues,
  type FulfillmentUpdateFormValues,
  FulfillmentMethod,
  FulfillmentCarrier,
} from "@/types/fulfillment-type";
import { createFulfillment, updateFulfillment } from "@/api/fulfillment-api";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { routes } from "@/routes/route-constants";
import { FormHandlers, useExposeFormHandlers } from "@/hooks/use-form-handlers";

export type FulfillmentFormHandlers = FormHandlers;

type FormMode = "create" | "update";

interface FulfillmentFormProps {
  mode: FormMode;
  initialValues?: Partial<Fulfillment> & { id?: string };
  onSuccess?: (saved: Fulfillment) => void;
  onCancel?: () => void;
  onFormReady?: (handlers: {
    handleSubmit: () => void;
    isSubmitting: boolean;
  }) => void;
}

const fulfillmentMethodOptions = [
  { value: FulfillmentMethod.PICKUP, label: "Pickup" },
  { value: FulfillmentMethod.DELIVERY_CITY, label: "Delivery City" },
  { value: FulfillmentMethod.DELIVERY_PROVINCE, label: "Delivery Province" },
];

const fulfillmentCarrierOptions = [
  { value: FulfillmentCarrier.VIREAK_BUNTHAM, label: "Vireak Buntham" },
  { value: FulfillmentCarrier.JNT, label: "JNT" },
];

export function FulfillmentForm({
  mode,
  initialValues,
  onSuccess,
  onCancel,
  onFormReady,
}: FulfillmentFormProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const isCreate = mode === "create";

  const resolver = zodResolver(
    isCreate ? fulfillmentCreateSchema : fulfillmentUpdateSchema
  );

  const {
    control,
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<FulfillmentCreateFormValues | FulfillmentUpdateFormValues>({
    resolver,
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      orderId: initialValues?.orderId ?? "",
      method: initialValues?.method ?? FulfillmentMethod.PICKUP,
      carrier: initialValues?.carrier ?? FulfillmentCarrier.VIREAK_BUNTHAM,
      price: initialValues?.price ?? "",
    },
  });

  const onSubmit: SubmitHandler<
    FulfillmentCreateFormValues | FulfillmentUpdateFormValues
  > = useCallback(
    async (values) => {
      try {
        let saved: any;
        const submitData = {
          ...values,
        };

        if (isCreate) {
          saved = await createFulfillment(
            submitData as FulfillmentCreateRequest
          );
        } else {
          const id = (initialValues as any)?.id as string | undefined;
          if (!id) {
            throw new Error("Missing id for update");
          }
          saved = await updateFulfillment(
            id,
            submitData as FulfillmentUpdateFormValues
          );
        }

        toast({
          title: isCreate ? "Created successfully" : "Updated successfully",
        });
        onSuccess?.(saved?.data ?? saved);
        if (!onSuccess) {
          setLocation(routes.fulfillment.list());
        }
      } catch (error: any) {
        toast({
          title: "Operation failed",
          description: error?.message ?? undefined,
          variant: "destructive",
        });
      }
    },
    [isCreate, initialValues, toast, onSuccess, setLocation]
  );

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setLocation(routes.fulfillment.list());
    }
  };

  // Expose form handlers to parent component
  const submitHandler = useCallback(
    () => handleSubmit(onSubmit)(),
    [handleSubmit, onSubmit]
  );

  useExposeFormHandlers({
    onFormReady,
    createSubmit: submitHandler,
    isSubmitting,
  });

  // Lightweight section wrapper for consistent spacing and headings
  const FormSection = React.useCallback(
    ({
      title,
      description,
      children,
    }: {
      title: string;
      description?: string;
      children: React.ReactNode;
    }) => (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: description ? 0.5 : 1 }}>
          {title}
        </Typography>
        {description ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {description}
          </Typography>
        ) : null}
        {children}
      </Box>
    ),
    []
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <FormSection title="Basic Information">
                <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Order ID"
                      placeholder="e.g. 123456"
                      InputLabelProps={{ shrink: true }}
                      {...register("orderId" as any)}
                      error={!!errors.orderId}
                      helperText={errors.orderId?.message as string}
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <FormControl fullWidth error={!!errors.method}>
                      <InputLabel id="fulfillment-method-label">
                        Fulfillment Method
                      </InputLabel>
                      <Controller
                        name={"method" as any}
                        control={control}
                        render={({ field }) => (
                          <Select
                            labelId="fulfillment-method-label"
                            label="Fulfillment Method"
                            value={(field.value as FulfillmentMethod) ?? ""}
                            onChange={field.onChange}
                          >
                            {fulfillmentMethodOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.method?.message && (
                        <FormHelperText>
                          {errors.method.message as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <FormControl fullWidth error={!!errors.carrier}>
                      <InputLabel id="fulfillment-carrier-label">
                        Carrier
                      </InputLabel>
                      <Controller
                        name={"carrier" as any}
                        control={control}
                        render={({ field }) => (
                          <Select
                            labelId="fulfillment-carrier-label"
                            label="Carrier"
                            value={(field.value as FulfillmentCarrier) ?? ""}
                            onChange={field.onChange}
                          >
                            {fulfillmentCarrierOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {errors.carrier?.message && (
                        <FormHelperText>
                          {errors.carrier.message as string}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Price"
                      placeholder="e.g. 10.50"
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      {...register("price" as any)}
                      error={!!errors.price}
                      helperText={errors.price?.message as string}
                      inputProps={{
                        step: "0.01",
                        min: "0",
                      }}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </form>
  );
}

export default FulfillmentForm;
